import type { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpResponseBase } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationStore } from '@indutrust/shared-data';
import { catchError, map, throwError } from 'rxjs';
import { navigateOrReport } from '../util/navigate-or-report.util';

const IGNORE_REQUEST_PREFIXES = ['/assets/i18n'];
const IGNORE_REQUEST_SUFFIXES = ['/session'];

function getRetryCount(req: HttpRequest<unknown>): number {
  return Number(req.headers.get('X-Retry-Count')) || 0;
}

function addToken(
  request: HttpRequest<unknown>,
  token: string | null
): HttpRequest<unknown> {
  if (token === null || token.length === 0) {
    return request;
  }
  return request.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

function isLoginAttempt(url: string): boolean {
  return IGNORE_REQUEST_SUFFIXES.some((suffix) => url.endsWith(suffix));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationStore = inject(NotificationStore);
  const router = inject(Router);
  const token =
    typeof localStorage === 'undefined'
      ? null
      : localStorage.getItem('access_token');

  let modifiedRequest = req.clone();

  const alreadyHasAuth = req.headers.has('Authorization');

  if (
    !alreadyHasAuth &&
    !IGNORE_REQUEST_SUFFIXES.some((suffix) => req.url.endsWith(suffix)) &&
    !IGNORE_REQUEST_PREFIXES.some((prefix) => req.url.startsWith(prefix))
  ) {
    modifiedRequest = addToken(modifiedRequest, token);
  }

  return next(modifiedRequest).pipe(
    map((event) => {
      return event;
    }),
    catchError((error: unknown) => {
      const retryCount = getRetryCount(modifiedRequest);

      if (error instanceof HttpResponseBase && error.status !== 404) {
        const loginAttempt = isLoginAttempt(modifiedRequest.url);

        if (
          (error.status === 401 ||
            error.status === 403 ||
            error.status === 302) &&
          retryCount === 0 &&
          !modifiedRequest.url.includes('/logout') &&
          !loginAttempt
        ) {
          if (error.status === 401) {
            notificationStore.notify({
              config: {
                messageKey: 'sessionExpired.message',
                scope: 'global',
                titleKey: 'sessionExpired.title',
                type: 'warn',
              },
            });
          }
          navigateOrReport(router, ['/login'], 'Navigation to login failed');
        }
      }

      return throwError(() => error);
    })
  );
};
